import { Dialog, DialogContent, DialogTitle, Tooltip } from "@mui/material";
import { ColDef, ColumnApi, GridApi, ICellRendererParams } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PullToRefresh from "react-simple-pull-to-refresh";
import { AddContactIcon, CancelIcon, EditContactIcon, TrashIcon } from "../../icon";
import { setBackHandler, setHeaderCount, setRefreshHandler } from "../../helpers/header.helper";
import setToast from "../../helpers/toaster.helper";
import { AppRoutes } from "../routes/route.path";
import * as selector from "../../selectors/selectors";
import { CustomAttributeDefinitionModel, CustomAttributeFieldType, CustomAttributeOptionModel, FIELD_TYPE_LABELS, fieldTypeToNumber, numberToFieldType } from "../../models/custom-attribute.model";
import { ContactService } from "../../services/ContactService";
import { ActionLabels, appNote, buttonLabels, emptyLabels } from "../shared/constants";
import { ToasterType } from "../shared/toaster/toaster.model";
import ConfirmDialog from "../shared/confirm-dialog.component";
import { OptionsData } from "../../models/grid.model";
import GridComponent from "../shared/grid";
import { MenuItemRenderer } from "../shared/grid/actionsMenuItemRendrer";
import PageHeaderComponent from "../shared/page-header.component";
import CustomAttributeDialog, { CustomAttributeDraft } from "./custom-attribute-view.component";
import { LoadCustomAttributeDefinitions } from "../../actions";

const FieldTypeIcon = ({ type }: { type: string }) => {
	const t = (type || "").toLowerCase();
	const icon =
		t.includes("text") ? "T" :
			t.includes("number") ? "#" :
				t.includes("date") ? "📅" :
					t.includes("boolean") ? "☑" :
						t.includes("drop") ? "▾" : "•";

	return (
		<span
			className="d-flex align-center justify-center br-50 bg-color-secondary-btn-highlight-hover"
			style={{ width: 24, height: 24, fontSize: "0.75rem" }}
		>
			{icon}
		</span>
	);
};

const CustomAttributesComponent = () => {
	const dispatch = useDispatch();
	const IsMobileDevice: boolean = useSelector(selector.isMobileDevice);
	const CustomAttributeDefinitions = useSelector(selector.customAttributeFields)
	const navigate = useNavigate();
	const [gridApi, setGridApi] = useState<GridApi>();
	const [columnApi, setColumnApi] = useState<ColumnApi | null>(null);
	const [searchValue, setSearchValue] = useState("");
	const [mobileMenu, setMobileMenu] = useState(false);
	const [clickRowData, setClickRowData] = useState<CustomAttributeDefinitionModel | null>(null);
	const [showAttributeDialog, setShowAttributeDialog] = useState(false);
	const [editingAttribute, setEditingAttribute] = useState<CustomAttributeDefinitionModel | null>(null);
	const [attributes, setAttributes] = useState<CustomAttributeDefinitionModel[]>([]);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [attributeToDelete, setAttributeToDelete] = useState<CustomAttributeDefinitionModel | null>(null);
	const [deleteLoading, setDeleteLoading] = useState(false);

	const fetchAttributes = useCallback(async () => {
		try {
			const active = CustomAttributeDefinitions.filter((x) => !x.IsDeleted);
			setAttributes(active);
		} catch (err) {
			console.log("err:", err);
			setAttributes([]);
		}
	}, [CustomAttributeDefinitions]);

	useEffect(() => {
		if (CustomAttributeDefinitions.length === 0) {
			LoadCustomAttributeDefinitions(dispatch)
		}
	}, []);

	useEffect(() => {
		fetchAttributes();
	}, [CustomAttributeDefinitions]);

	useEffect(() => {
		setBackHandler(() => navigate(AppRoutes.CONTACTS));
	}, [navigate]);

	useEffect(() => {
		setHeaderCount(attributes.length);
	}, [attributes]);

	useEffect(() => {
		setRefreshHandler(() => gridRefresh());
	}, [gridApi, columnApi]);

	const onGridReady = (params: any) => {
		setGridApi(params.api);
		setColumnApi(params.columnApi);
	};

	const searchAttributes = (e: any) => {
		const val = e.target.value;
		setSearchValue(val);
		gridApi?.setQuickFilter(val);
	};

	const gridRefresh = async () => {
		LoadCustomAttributeDefinitions(dispatch);
		columnApi?.resetColumnState();
		gridApi?.refreshCells({ force: true });
		gridApi?.deselectAll();
	};

	const pullToRefresh = () =>
		new Promise<void>((resolve) => setTimeout(resolve, 15)).then(() => LoadCustomAttributeDefinitions(dispatch));

	const handleRowClick = (row: CustomAttributeDefinitionModel) => {
		if (IsMobileDevice) {
			setMobileMenu(true);
			setClickRowData(row);
		}
	};

	const closeMobileMenu = () => {
		setMobileMenu(false);
		setClickRowData(null);
	};

	const openAddDialog = () => {
		setEditingAttribute(null);
		setShowAttributeDialog(true);
	};

	const openEditDialog = (row: CustomAttributeDefinitionModel) => {
		setEditingAttribute(row);
		setShowAttributeDialog(true);
	};

	const closeAttributeDialog = () => {
		setShowAttributeDialog(false);
		setEditingAttribute(null);
	};

	const buildOptionsForApi = (
		draft: CustomAttributeDraft,
		editing: CustomAttributeDefinitionModel | null
	): CustomAttributeOptionModel[] => {
		const oldOptions: CustomAttributeOptionModel[] = (editing?.OptionsDetail ?? []).map((option) => ({
			Id: option.id,
			Label: option.label,
			IsDeleted: false,
		}));

		if (draft.fieldType !== "DROPDOWN") {
			return oldOptions.map((option) => ({
				Id: option.Id,
				Label: option.Label,
				IsDeleted: true,
			}));
		}

		const newLabels = (draft.dropdownOptions ?? [])
			.map((label) => label.trim())
			.filter(Boolean)
			.filter((label, index, arr) => arr.indexOf(label) === index);

		const activeOptions = newLabels.map((label) => {
			const oldMatch = oldOptions.find((option) => option.Label === label);
			return {
				Id: oldMatch?.Id ?? "",
				Label: label,
				IsDeleted: false,
			};
		});

		const deletedOptions = oldOptions
			.filter((option) => !newLabels.includes(option.Label))
			.map((option) => ({
				Id: option.Id,
				Label: option.Label,
				IsDeleted: true,
			}));

		return [...activeOptions, ...deletedOptions];
	};

	const buildReference = (name: string): string => {
		return name
			.trim()
			.split(/\s+/)
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join("");
	};

	const buildPayload = (
		draft: CustomAttributeDraft,
		id: string,
		editing: CustomAttributeDefinitionModel | null
	) => ({
		Id: id,
		Name: draft.name.trim(),
		Reference: buildReference(draft.name),
		IsDeleted: false,
		Type: fieldTypeToNumber(draft.fieldType as CustomAttributeFieldType),
		Options: buildOptionsForApi(draft, editing),
		Description: draft.helpText?.trim() ?? "",
		IsRequired: draft.required,
	});

	const upsertAttribute = async (draft: CustomAttributeDraft) => {
		// const apiPayload = buildPayload(draft, editingAttribute?.Id ?? "", editingAttribute);

		// if (editingAttribute) {
		// 	await ContactService.updateCustomAttribute(apiPayload);

		// } else {
		// 	await ContactService.createCustomAttribute(apiPayload);
		// }
		closeAttributeDialog();
	};

	const openDeleteConfirmDialog = (row: CustomAttributeDefinitionModel) => {
		setAttributeToDelete(row);
		setShowDeleteConfirm(true);
		setMobileMenu(false);
	};

	const closeDeleteConfirmDialog = useCallback(async (isConfirm: boolean) => {
		if (!isConfirm || !attributeToDelete) {
			setShowDeleteConfirm(false);
			setAttributeToDelete(null);
			return;
		}

		const deletedId = attributeToDelete.Id;
		setDeleteLoading(true);

		try {
			await ContactService.deleteCustomAttribute(deletedId);
			setToast("Custom attribute deleted successfully.", ToasterType.SUCCESS);
		} catch (err: any) {
			setToast(err?.response?.data?.message ?? "Delete failed", ToasterType.DANGER);
		} finally {
			setShowDeleteConfirm(false);
			setAttributeToDelete(null);
			setDeleteLoading(false);
		}
	}, [attributeToDelete]);

	const getMenuActions = (data: CustomAttributeDefinitionModel): OptionsData[] => {
		return [
			{
				Name: ActionLabels.EDIT,
				Icon: EditContactIcon,
				OnClick: () => openEditDialog(data),
			},
			{
				Name: ActionLabels.DELETE,
				Icon: TrashIcon,
				OnClick: () => openDeleteConfirmDialog(data),
			},
		];
	};

	const columnDefs = useMemo<ColDef[]>(() => [
		{
			field: "Name",
			headerName: "Attribute Name",
			minWidth: IsMobileDevice ? undefined : 200,
			flex: 1,
			resizable: true,
		},
		// {
		// 	field: "FieldType",
		// 	headerName: "Field Type",
		// 	minWidth: IsMobileDevice ? undefined : 160,
		// 	cellRenderer: (params: ICellRendererParams) => (
		// 		<span className="d-flex align-center">
		// 			<FieldTypeIcon type={params.value} />
		// 			<span className="ml-2">{params.value}</span>
		// 		</span>
		// 	),
		// 	resizable: true,
		// },
		{
			field: "Type",
			headerName: "Field Type",
			minWidth: IsMobileDevice ? undefined : 160,
			valueGetter: (params) => {
				const code = numberToFieldType(params.data?.Type ?? 0);
				return FIELD_TYPE_LABELS[code];
			},
			cellRenderer: (params: ICellRendererParams) => {
				const label = FIELD_TYPE_LABELS[numberToFieldType(params.data?.Type ?? 0)];
				return (
					<span className="d-flex align-center">
						<FieldTypeIcon type={label} />
						<span className="ml-2">{label}</span>
					</span>
				);
			},
			resizable: true,
		},
		{
			field: "IsRequired",
			headerName: "Is Required",
			minWidth: IsMobileDevice ? undefined : 100,
			cellRenderer: (params: ICellRendererParams) => (params.value ? "Yes" : "No"),
			resizable: true,
		},
		{
			field: "Action",
			maxWidth: 130,
			headerName: "Action",
			cellRenderer: (params: ICellRendererParams<CustomAttributeDefinitionModel>) => (
				<div className="d-flex">
					{getMenuActions(params.data)
						.filter((x) => !x.Hide)
						.map((x) => (
							<div key={x.Name}>
								<Tooltip title={x.Name} placement="top">
									<x.Icon
										className="ml-2 c-pointer"
										onClick={(e: any) => {
											e.stopPropagation();
											x.OnClick();
										}}
									/>
								</Tooltip>
							</div>
						))}
				</div>
			),
		},
	], [IsMobileDevice]);

	return (
		<>
			<PullToRefresh isPullable={IsMobileDevice} pullingContent="" onRefresh={pullToRefresh}>
				<div className="d-flex flex-direction h-p-100 o-hidden justify-between">
					<PageHeaderComponent
						GridTitle=""
						Count={attributes.length}
						Actions={[
							{
								OnClick: openAddDialog,
								ButtonLabel: buttonLabels.ADD_CUSTOM_ATTRIBUTES,
								Icon: IsMobileDevice ? "" : AddContactIcon,
								Variant: "primary-btn",
							},
						]}
						Reload={() => LoadCustomAttributeDefinitions(dispatch)}
						ShowFilterIcon={false}
						searchValue={searchValue}
						onSearch={(e) => searchAttributes(e)}
						PlaceHolder="Search attributes..."
					/>

					<GridComponent
						ColumnDefs={columnDefs}
						RowData={attributes}
						OnGridReady={onGridReady}
						OnGridRowClick={handleRowClick}
						OnGridCellRowClick={(e: any, row: CustomAttributeDefinitionModel) => {
							if (!IsMobileDevice && e.colDef.field !== "Action") {
								openEditDialog(row);
							}
						}}
						IsPaginationEnabled={!IsMobileDevice}
						EmptyLabel={emptyLabels.CUSTOM_ATTRIBUTES}
						LockVisible={IsMobileDevice}
						gridApi={gridApi}
						ShowRowPointer={true}
						isHorizontalScroll={true}
					/>

					{IsMobileDevice && mobileMenu && clickRowData && (
						<Dialog
							fullWidth
							onClose={closeMobileMenu}
							aria-labelledby="responsive-dialog-title"
							open={mobileMenu}
							classes={{ paper: "br-2 dialogue-shadow w-p-70 bg-color-menu-bg" }}
						>
							<DialogTitle className="dialog-header pb-1">
								<div className="h1 color-display-title d-flex" id="customized-dialog-title">
									{clickRowData.Name}
									<Tooltip title="Close" placement="bottom">
										<span className="ml-auto">
											<CancelIcon
												className="c-pointer"
												onClick={(e: any) => {
													e.stopPropagation();
													closeMobileMenu();
												}}
											/>
										</span>
									</Tooltip>
								</div>
							</DialogTitle>

							<DialogContent className="mln-1 o-hidden pt-0">
								<MenuItemRenderer
									MenuData={getMenuActions(clickRowData)}
									OnClick={() => closeMobileMenu()}
								/>
							</DialogContent>
						</Dialog>
					)}
				</div>
			</PullToRefresh>

			<CustomAttributeDialog
				show={showAttributeDialog}
				onClose={() => closeAttributeDialog()}
				onSave={(draft) => upsertAttribute(draft)}
				initialValue={editingAttribute}
				onAttributeSave={() => { }}
			/>

			{showDeleteConfirm && (
				<ConfirmDialog
					Show={showDeleteConfirm}
					onClose={closeDeleteConfirmDialog}
					Text={appNote.DELETE_CUSTOM_ATTRIBUTE}
					ButtonName={ActionLabels.DELETE}
					Loading={deleteLoading}
					IsMobileDevice={IsMobileDevice}
				/>
			)}
		</>
	);
};

export default CustomAttributesComponent;